import { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { api } from '@/lib/api';
import { errMsg } from './data';

/** Shape returned by POST media/upload (API_CHANGES §9). */
export interface UploadedMedia { id: number; url: string; path: string; name: string; mime: string; size: number; created_at?: string }

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Multipart upload to `media/upload`. `related` optionally links the file to a record. */
export async function uploadMedia(file: File, related?: { type: string; id?: number | string }): Promise<UploadedMedia> {
  const fd = new FormData();
  fd.append('file', file);
  if (related?.type) fd.append('related_type', related.type);
  if (related?.id != null) fd.append('related_id', String(related.id));
  const r = await api.post('/media/upload', fd);
  return (r.data?.data ?? r.data) as UploadedMedia;
}

const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/**
 * File picker that uploads straight away and hands back the stored media record.
 * `value` is the currently stored path/url (so edit forms can show what is saved).
 */
export function FileUpload({ label, accept, value, onUploaded, onClear, hint, related }: {
  label: string;
  accept?: string;
  value?: string | null;
  onUploaded: (m: UploadedMedia) => void;
  onClear?: () => void;
  hint?: string;
  related?: { type: string; id?: number | string };
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');

  async function pick(file?: File) {
    setErr('');
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) return setErr(`The file is ${kb(file.size)}. The limit is 10 MB.`);
    setBusy(true);
    try {
      const m = await uploadMedia(file, related);
      setName(`${m.name || file.name} (${kb(m.size || file.size)})`);
      onUploaded(m);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  const shown = name || (value ? value.split('/').pop() : '');
  return (
    <div className="block text-sm">
      <span className="block mb-1 text-slate-600 font-medium">{label}</span>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" disabled={busy} onClick={() => input.current?.click()} className="inline-flex items-center gap-2 border rounded-xl px-3 py-2 bg-white hover:bg-slate-50 disabled:opacity-50">
          <Paperclip size={14} /> {busy ? 'Uploading…' : shown ? 'Replace file' : 'Choose file'}
        </button>
        {shown && (
          <span className="inline-flex items-center gap-1 text-slate-600">
            {shown}
            {onClear && !busy && (
              <button type="button" aria-label="Remove file" className="text-slate-400 hover:text-red-600" onClick={() => { setName(''); onClear(); }}><X size={14} /></button>
            )}
          </span>
        )}
      </div>
      <input ref={input} type="file" accept={accept} className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
      {hint && !err && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
      {err && <span className="block text-xs text-red-600 mt-1">{err}</span>}
    </div>
  );
}
